import React, { useState, useEffect } from 'react'
import { Table, Button, Modal, Form, Input, Switch, Space, Tag, Alert, Tooltip } from 'antd'
import { PlusOutlined, DeleteOutlined, ReloadOutlined, LinkOutlined, DisconnectOutlined } from '@ant-design/icons'
import { apiRequest } from '../../helpers'
import { useSearchParams } from 'react-router-dom'

const apiUrl = process.env.REACT_APP_API

const GmailConnectors = () => {
  const [connectors, setConnectors] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  const [modalOpen, setModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selected, setSelected] = useState(null)
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)

  const [searchParams] = useSearchParams()

  useEffect(() => {
    fetchConnectors()
    if (searchParams.get('connected') === 'true') {
      setSuccess('Cont Gmail conectat cu succes!')
      setTimeout(() => setSuccess(null), 5000)
    }
    if (searchParams.get('error') === 'true') {
      setError('Eroare la conectarea contului Gmail. Incearca din nou.')
    }
  }, [])

  const fetchConnectors = async () => {
    setLoading(true)
    try {
      const res = await apiRequest(`${apiUrl}connector/gmail`, 'GET')
      setConnectors(res?.data?.data || [])
    } catch { setError('Eroare la incarcare.') }
    finally { setLoading(false) }
  }

  const openCreate = () => {
    form.resetFields()
    form.setFieldsValue({ active: true })
    setModalOpen(true)
  }

  const handleSave = async () => {
    const values = await form.validateFields()
    setSaving(true)
    try {
      await apiRequest(`${apiUrl}connector/gmail`, 'POST', values)
      setModalOpen(false)
      fetchConnectors()
    } catch { setError('Eroare la salvare.') }
    finally { setSaving(false) }
  }

  const handleDelete = async () => {
    try {
      await apiRequest(`${apiUrl}connector/gmail/${selected.id}`, 'DELETE')
      setDeleteModalOpen(false)
      fetchConnectors()
    } catch { setError('Eroare la stergere.') }
  }

  const handleConnect = (connector) => {
    window.location.href = `${apiUrl}connector/gmail/${connector.id}/auth`
  }

  const handleDisconnect = async (connector) => {
    try {
      await apiRequest(`${apiUrl}connector/gmail/${connector.id}/disconnect`, 'POST', {})
      setSuccess(`Contul ${connector.email} a fost deconectat.`)
      setTimeout(() => setSuccess(null), 3000)
      fetchConnectors()
    } catch { setError('Eroare la deconectare.') }
  }

  const columns = [
    { title: 'Nume', dataIndex: 'name', key: 'name' },
    { title: 'Email conectat', dataIndex: 'email', key: 'email', render: (v) => v || <span style={{ color: '#aaa' }}>—</span> },
    {
      title: 'Status', key: 'connected',
      render: (_, c) => <Tag color={c.connected ? 'green' : 'default'}>{c.connected ? 'Conectat' : 'Neconectat'}</Tag>
    },
    {
      title: 'Activ', key: 'active',
      render: (_, c) => <Tag color={c.active ? 'green' : 'red'}>{c.active ? 'Activ' : 'Inactiv'}</Tag>
    },
    {
      title: 'Actiuni', key: 'actions', width: 200,
      render: (_, c) => (
        <Space>
          {!c.connected ? (
            <Button size="small" type="primary" icon={<LinkOutlined />} onClick={() => handleConnect(c)}>
              Conecteaza
            </Button>
          ) : (
            <Button size="small" icon={<DisconnectOutlined />} onClick={() => handleDisconnect(c)}>
              Deconecteaza
            </Button>
          )}
          <Tooltip title="Sterge">
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => { setSelected(c); setDeleteModalOpen(true) }} />
          </Tooltip>
        </Space>
      )
    },
  ]

  return (
    <div>
      <div style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <strong style={{ fontSize: 16 }}>Conectori Gmail</strong>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Adauga cont</Button>
            <Button icon={<ReloadOutlined />} onClick={fetchConnectors} />
          </Space>
        </div>

        {error && <Alert message={error} type="error" showIcon closable onClose={() => setError(null)} style={{ marginBottom: 12 }} />}
        {success && <Alert message={success} type="success" showIcon style={{ marginBottom: 12 }} />}

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Cum conectezi un cont Gmail"
          description={
            <ol style={{ marginBottom: 0, marginTop: 4 }}>
              <li>Adauga un conector cu butonul "Adauga cont"</li>
              <li>Click pe butonul <strong>Conecteaza</strong> de langa conector</li>
              <li>Vei fi redirectat la Google pentru autorizare</li>
              <li>Selecteaza contul Gmail si accepta permisiunile</li>
              <li>Esti redirectat inapoi — contul apare ca <strong>Conectat</strong></li>
            </ol>
          }
        />

        <Table
          columns={columns}
          dataSource={connectors}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={false}
          locale={{ emptyText: 'Nu exista conectori Gmail.' }}
        />
      </div>

      {/* Modal Adauga */}
      <Modal
        title="Adauga cont Gmail"
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText={saving ? 'Se salveaza...' : 'Salveaza'}
        cancelText="Anuleaza"
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Nume" name="name" rules={[{ required: true, message: 'Numele este obligatoriu' }]}>
            <Input placeholder="ex: Gmail Comercial" />
          </Form.Item>
          <small style={{ color: '#888', display: 'block', marginTop: -16, marginBottom: 16 }}>
            Un nume descriptiv pentru acest cont (emailul se completeaza automat dupa conectare)
          </small>
          <Form.Item name="active" valuePropName="checked">
            <Switch checkedChildren="Activ" unCheckedChildren="Inactiv" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Delete */}
      <Modal
        title="Confirmare stergere"
        open={deleteModalOpen}
        onOk={handleDelete}
        onCancel={() => setDeleteModalOpen(false)}
        okText="Sterge"
        okButtonProps={{ danger: true }}
        cancelText="Anuleaza"
      >
        <p>Stergi conectorul <strong>{selected?.name}</strong>?</p>
      </Modal>
    </div>
  )
}

export default GmailConnectors
